import {
  AspectRatio,
  Body,
  Box,
  Heading,
  Image,
  Label,
  Radio,
  RadioGroup,
  Sheet,
  VStack,
  useToast,
} from '@hover/blueprint';
import React, { useCallback, useState } from 'react';

import {
  useAttachableScans,
  useScanImage,
} from 'src/modules/homeownerApp/components/Projects/Project/hooks';
import {
  useErrors,
  useHomeownerAppContext,
} from 'src/modules/homeownerApp/hooks';
import { Button } from 'src/shared/components';
import { updateScanToProjectUpdater } from 'src/shared/graphql/cache/Project';
import type { BasicJobInfoFragment, Job } from 'src/shared/graphql/generated';
import {
  useAttachScanToProjectMutation,
  useRemoveScanFromProjectMutation,
} from 'src/shared/graphql/generated';
import { TranslationNamespace, useTranslate } from 'src/shared/hooks';

const ScanCard = ({ scan }: { scan: Job }) => {
  const displayImage =
    useScanImage(scan.bestFrontImage, scan.images) ?? undefined;

  return (
    <Label box cursor="pointer" py="200" width="1">
      <Box>
        <AspectRatio height="80px" ratio={1} width="80px">
          <Image
            alt="scan display image"
            borderRadius="300"
            height="100%"
            objectFit="cover"
            src={displayImage}
            width="100%"
          />
        </AspectRatio>
      </Box>
      <VStack alignItems="flex-start" flex={2} px="500" py="400">
        <Body fontSize="200" fontWeight="700" m={0}>
          {scan.name}
        </Body>
        <Body color="neutral.400" fontSize="200" mt="0 !important">
          {scan.locationLine1}
        </Body>
      </VStack>
      <Box>
        <Radio size="large" value={String(scan.id)} variant="filled" />
      </Box>
    </Label>
  );
};

type UpdateScanMenuProps = {
  projectId: string;
  attachedScan: BasicJobInfoFragment;
  closeMenu: () => void;
};

export const UpdateScanMenu = ({
  projectId,
  attachedScan,
  closeMenu,
}: UpdateScanMenuProps) => {
  const { trackingData } = useHomeownerAppContext();
  const attachedScanId = String(attachedScan.id);
  const toast = useToast();
  const { showError } = useErrors();
  const [selectedScanId, setSelectedScanId] = useState(attachedScanId);
  const t = useTranslate(TranslationNamespace.HOMEOWNER_APP);
  const disableApplyButton = attachedScanId === selectedScanId;
  const [removeScan, { loading: removeLoading }] =
    useRemoveScanFromProjectMutation();
  const [attachScan, { loading: attachScanLoading }] =
    useAttachScanToProjectMutation();
  const attachableScans = useAttachableScans();

  const onApply = useCallback(async () => {
    if (selectedScanId === attachedScanId) {
      return;
    }

    const removeScanResult = await removeScan({
      variables: {
        projectId: Number(projectId),
        scanId: attachedScan.id,
      },
    });

    if (removeScanResult.errors) {
      showError({
        error: new Error(
          removeScanResult.errors.map(({ message }) => message).join(' '),
        ),
        formatError: message =>
          t('projects.confirmation.removeScan.error', { message }),
      });
      return;
    }

    const selectedScan = attachableScans.find(
      ({ id }) => id === Number(selectedScanId),
    );

    if (!selectedScan) {
      showError({
        error: new Error('Selected scan not found'),
        formatError: errorMessage =>
          t('projects.confirmation.attachScan.error', { errorMessage }),
      });
      return;
    }

    const attachNewScan = await attachScan({
      variables: {
        projectId,
        scanId: Number(selectedScanId),
      },
      update: updateScanToProjectUpdater({
        projectIdToUpdate: Number(projectId),
        scan: selectedScan,
        removedScanId: attachedScan.id,
      }),
    });

    if (attachNewScan.errors) {
      showError({
        error: new Error('Failed to attach scan to project'),
        formatError: errorMessage =>
          t('projects.confirmation.attachScan.error', { errorMessage }),
      });
      return;
    }

    toast({
      props: {
        message: t('projects.scanUpdated'),
        colorScheme: 'dark',
      },
    });
    closeMenu();
  }, [
    selectedScanId,
    attachedScanId,
    removeScan,
    projectId,
    attachedScan.id,
    attachableScans,
    attachScan,
    toast,
    t,
    showError,
    closeMenu,
  ]);

  if (!attachableScans.length) {
    return null;
  }

  return (
    <Sheet.Contents
      contentKey="update-scans"
      height={{ base: 'auto', tablet: 'calc(100vh - 88px)' }}
    >
      <VStack
        alignItems="flex-start"
        height={{ base: 'auto', tablet: 'calc(100vh - 88px)' }}
        w="100%"
      >
        <Box>
          <Heading>{t('projects.confirmation.selectNewScan.header')}</Heading>
        </Box>
        <RadioGroup
          defaultValue={attachedScanId ?? undefined}
          onChange={setSelectedScanId}
          w="100%"
        >
          {attachableScans.map(scan => (
            <ScanCard key={scan.id} scan={scan} />
          ))}
        </RadioGroup>
        <Box
          alignItems="flex-end"
          flex={1}
          pb={{ base: '400', tablet: '700' }}
          pt="500"
          w="100%"
        >
          <Button
            disabled={disableApplyButton}
            isLoading={removeLoading || attachScanLoading}
            label={t('projects.confirmation.selectNewScan.apply')}
            onClick={onApply}
            optionalTrackingProperties={{
              ...trackingData,
              track_id: 'select-different-scan-apply',
              job_id: attachedScan.id,
              button_location: 'My scans module',
              project_id: Number(projectId),
            }}
            w="100%"
          >
            {t('projects.confirmation.selectNewScan.apply')}
          </Button>
        </Box>
      </VStack>
    </Sheet.Contents>
  );
};
